import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback } from 'react';

import type { FiatCurrency } from '#src/__generated__/globalTypes.ts';
import type { GraphQLResult } from '#src/components/form/Form/index.tsx';
import { fiatAccounts } from '#src/contexts/currentUser/queries.ts';
import { useMutation } from '#src/hooks/graphql/useMutation.ts';

import type {
  CreateFiatWalletMutation,
  CreateFiatWalletMutationVariables,
  UpdateFiatWalletMutation,
  UpdateFiatWalletMutationVariables,
} from './__generated__/useCreateOrUpdateFiatWallet.graphql.ts';

const CREATE_FIAT_WALLET_MUTATION = gql`
  mutation CreateFiatWalletMutation($input: createFiatWalletInput!) {
    createFiatWallet(input: $input) {
      currentUser {
        slug
        profile {
          id
          enabledWallets
        }
        mangopayUser {
          id
          scaEnrollmentState
        }
        ...CurrentUseProvider_fiatAccounts
      }
      errors {
        code
        message
      }
    }
  }
  ${fiatAccounts}
` as TypedDocumentNode<
  CreateFiatWalletMutation,
  CreateFiatWalletMutationVariables
>;

const UPDATE_FIAT_WALLET_MUTATION = gql`
  mutation UpdateFiatWalletMutation($input: updateFiatWalletInput!) {
    updateFiatWallet(input: $input) {
      currentUser {
        slug
        profile {
          id
          enabledWallets
        }
        mangopayUser {
          id
          scaEnrollmentState
        }
        ...CurrentUseProvider_fiatAccounts
      }
      errors {
        code
        message
      }
    }
  }
  ${fiatAccounts}
` as TypedDocumentNode<
  UpdateFiatWalletMutation,
  UpdateFiatWalletMutationVariables
>;

type UpdateFiatWalletArgs = {
  dob?: Date;
  firstName?: string;
  lastName?: string;
};

export type CreateFiatWalletArgs = {
  dob: Date;
  firstName: string;
  lastName: string;
  currency: FiatCurrency;
  countryOfResidenceCode: string;
  nationalityCode: string;
  mangopayTermsAndConditionsAccepted: boolean;
};

export type CreateResultType = GraphQLResult<
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  CreateFiatWalletMutation['createFiatWallet'] | {}
>;

export const useCreateOrUpdateFiatWallet: () => {
  create: (args: CreateFiatWalletArgs) => Promise<CreateResultType>;
  update: (args: UpdateFiatWalletArgs) => Promise<
    // eslint-disable-next-line @typescript-eslint/no-empty-object-type
    GraphQLResult<UpdateFiatWalletMutation['updateFiatWallet'] | {}>
  >;
  loading: boolean;
} = () => {
  const [createFiatWallet, { loading: createLoading }] = useMutation(
    CREATE_FIAT_WALLET_MUTATION,
    {
      showErrorsInForm: true,
      showErrorsWithSnackNotification: false,
    }
  );

  const [updateFiatWallet, { loading: updateLoading }] = useMutation(
    UPDATE_FIAT_WALLET_MUTATION,
    {
      showErrorsInForm: true,
      showErrorsWithSnackNotification: false,
    }
  );

  const loading = createLoading || updateLoading;

  const create = useCallback(
    async (args: CreateFiatWalletArgs) => {
      const { data } = await createFiatWallet({
        variables: {
          input: {
            ...args,
          },
        },
      });

      return data?.createFiatWallet || {};
    },
    [createFiatWallet]
  );

  const update = useCallback(
    async (args: UpdateFiatWalletArgs) => {
      const { data } = await updateFiatWallet({
        variables: {
          input: args,
        },
      });

      return data?.updateFiatWallet || {};
    },
    [updateFiatWallet]
  );

  return {
    create,
    update,
    loading,
  };
};
